import { GTM_SCRIPT_ID, TRACKING_EVENT_NAME } from '@/constants/tracking';

import type { TrackingEvent } from './events';

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Window への宣言マージ
  interface Window {
    // NOTE: GTM を読み込まないホスト (localhost 等) では未定義のまま。push はすべて no-op になる。
    dataLayer?: unknown[];
  }
}

/** 送信直後にページを離れる経路で、GTM の応答を待つ上限 (ms)。 */
const leavingEventTimeout = 1000;

export const initDataLayer = () => {
  // eslint-disable-next-line @silverhand/fp/no-mutation
  window.dataLayer ??= [];
};

const toDataLayerEvent = ({ category, action, label }: TrackingEvent) => ({
  event: TRACKING_EVENT_NAME,
  event_category: category,
  event_action: action,
  event_label: label,
});

/** NOTE: 計測の失敗で認証フローを止めないよう、push の例外はここで握りつぶす。 */
export const sendEvent = (trackingEvent: TrackingEvent) => {
  try {
    // eslint-disable-next-line @silverhand/fp/no-mutating-methods
    window.dataLayer?.push(toDataLayerEvent(trackingEvent));
  } catch {}
};

const isGtmPending = () => Boolean(document.querySelector(`#${GTM_SCRIPT_ID}:not([data-failed])`));

/**
 * {@link sendEvent} と同じものを push し、GTM がタグを処理し終えるまで (最長 {@link leavingEventTimeout}) 待つ。
 *
 * NOTE: リダイレクト直前用。GTM の script が無い・読み込みに失敗した場合は `eventCallback` が呼ばれないので待たない。
 * 読み込み中は、gtm.js の実行後に GTM が積まれたイベントを処理して callback を呼ぶので待つ。
 */
export const sendEventAndWait = async (trackingEvent: TrackingEvent) =>
  new Promise<void>((resolve) => {
    if (!window.dataLayer || !isGtmPending()) {
      sendEvent(trackingEvent);
      resolve();
      return;
    }

    const timeoutId = setTimeout(resolve, leavingEventTimeout);

    try {
      // eslint-disable-next-line @silverhand/fp/no-mutating-methods
      window.dataLayer.push({
        ...toDataLayerEvent(trackingEvent),
        eventCallback: () => {
          clearTimeout(timeoutId);
          resolve();
        },
      });
    } catch {
      clearTimeout(timeoutId);
      resolve();
    }
  });
